import React, { useState, useEffect, useCallback } from 'react';
import { apiFetch } from '../../services/api';
import './LicenseManagementPage.css';
import {
    Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Paper, Typography, Button, Switch, Box, TextField, Alert, Chip, LinearProgress
} from '@mui/material';
import { useOri } from '../../context/OriContext';
import LoadingIcon from '../LoadingIcon';

interface LicenseInfo {
    licensed: boolean;
    license?: {
        product: string;
        issued: string;
        registree: {
            company: string;
            firstName: string;
            lastName: string;
            email: string;
            phone: string;
        };
        features: string[];
        properties: {
            'max-devices': number;
        };
    };
    exp?: number;
    maxDevices: number;
    licensedDeviceCount: number;
    message?: string;
}

interface DeviceInfo {
    deviceId: string;
    deviceName: string;
    ori: string;
    licensed?: boolean;
}

const useIsSiteAdmin = () => {
    const groupsStr = sessionStorage.getItem('groups');
    if (!groupsStr) return false;
    try {
        return JSON.parse(groupsStr).includes('site-admin');
    } catch {
        return false;
    }
};

const LicenseManagementPage: React.FC = () => {
    const [licenseInfo, setLicenseInfo] = useState<LicenseInfo | null>(null);
    const [devices, setDevices] = useState<DeviceInfo[]>([]);
    const [isLoading, setIsLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);
    const [successMessage, setSuccessMessage] = useState<string | null>(null);
    const [licenseToken, setLicenseToken] = useState('');
    const [isSaving, setIsSaving] = useState(false);
    const [publicKey, setPublicKey] = useState('');
    const [hasPublicKey, setHasPublicKey] = useState(false);
    const [isEditingKey, setIsEditingKey] = useState(false);
    const [isKeyLoading, setIsKeyLoading] = useState(false);
    const { selectedOri } = useOri();
    const isSiteAdmin = useIsSiteAdmin();

    const fetchLicenseInfo = useCallback(async () => {
        if (!selectedOri) return;
        setIsLoading(true);
        setError(null);
        try {
            const [licenseResp, devicesResp] = await Promise.all([
                apiFetch(`/api/risc/license/agency?ori=${selectedOri}`),
                apiFetch(`/api/risc/device?ori=${selectedOri}`)
            ]);
            if (!licenseResp.ok) throw new Error('Failed to fetch license info');
            if (!devicesResp.ok) throw new Error('Failed to fetch devices');
            const licenseData = await licenseResp.json();
            const devicesData = await devicesResp.json();
            setLicenseInfo(licenseData);
            setDevices(devicesData);
        } catch (err) {
            setError(err instanceof Error ? err.message : 'An unknown error occurred.');
        } finally {
            setIsLoading(false);
        }
    }, [selectedOri]);

    const fetchPublicKey = useCallback(async () => {
        if (!isSiteAdmin) return;
        setIsKeyLoading(true);
        try {
            const resp = await apiFetch('/api/risc/license/key');
            if (resp.ok) {
                const data = await resp.json();
                setPublicKey(data.publicKey || '');
                setHasPublicKey(!!data.publicKey);
                setIsEditingKey(false);
            } else {
                setHasPublicKey(false);
            }
        } catch {
            setHasPublicKey(false);
        } finally {
            setIsKeyLoading(false);
        }
    }, [isSiteAdmin]);

    useEffect(() => {
        fetchLicenseInfo();
    }, [fetchLicenseInfo, selectedOri]);

    useEffect(() => {
        fetchPublicKey();
    }, [fetchPublicKey]);

    const handleSavePublicKey = async () => {
        if (!publicKey.trim()) return;
        setIsSaving(true);
        setError(null);
        setSuccessMessage(null);
        try {
            const resp = await apiFetch('/api/risc/license/key', {
                method: 'PUT',
                body: JSON.stringify({ publicKey: publicKey.trim() })
            });
            if (!resp.ok) {
                const data = await resp.json();
                throw new Error(data.error || 'Failed to save public key');
            }
            setSuccessMessage('Public key updated successfully.');
            setHasPublicKey(true);
            setIsEditingKey(false);
        } catch (err) {
            setError(err instanceof Error ? err.message : 'Failed to save public key.');
        } finally {
            setIsSaving(false);
        }
    };

    const handleApplyLicense = async () => {
        if (!licenseToken.trim() || !selectedOri) return;
        setIsSaving(true);
        setError(null);
        setSuccessMessage(null);
        try {
            const resp = await apiFetch('/api/risc/license/agency', {
                method: 'POST',
                body: JSON.stringify({ ori: selectedOri, token: licenseToken.trim() })
            });
            const data = await resp.json();
            if (!resp.ok) {
                throw new Error(data.error || 'Failed to apply license');
            }
            setSuccessMessage(`License applied: ${data.devicesLicensed}/${data.totalDevices} devices licensed.`);
            setLicenseToken('');
            await fetchLicenseInfo();
        } catch (err) {
            setError(err instanceof Error ? err.message : 'Failed to apply license.');
        } finally {
            setIsSaving(false);
        }
    };

    const handleDeleteLicense = async () => {
        if (!selectedOri || !window.confirm(`Are you sure you want to remove the license from ${selectedOri}? All devices will be unlicensed.`)) return;
        setIsSaving(true);
        setError(null);
        setSuccessMessage(null);
        try {
            const resp = await apiFetch('/api/risc/license/agency', {
                method: 'DELETE',
                body: JSON.stringify({ ori: selectedOri })
            });
            const data = await resp.json();
            if (!resp.ok) {
                throw new Error(data.error || 'Failed to delete license');
            }
            setSuccessMessage(data.message);
            await fetchLicenseInfo();
        } catch (err) {
            setError(err instanceof Error ? err.message : 'Failed to delete license.');
        } finally {
            setIsSaving(false);
        }
    };

    const handleToggleDeviceLicense = async (deviceId: string, licensed: boolean) => {
        if (!selectedOri) return;
        setError(null);
        setSuccessMessage(null);
        try {
            const resp = await apiFetch('/api/risc/license/device', {
                method: 'POST',
                body: JSON.stringify({ ori: selectedOri, deviceId, licensed })
            });
            const data = await resp.json();
            if (!resp.ok) {
                throw new Error(data.error || 'Failed to update device license');
            }
            await fetchLicenseInfo();
        } catch (err) {
            setError(err instanceof Error ? err.message : 'Failed to update device license.');
        }
    };

    const isExpired = licenseInfo?.exp ? licenseInfo.exp < Math.floor(Date.now() / 1000) : false;
    const licensedCount = devices.filter(d => d.licensed).length;
    const maxDevices = licenseInfo?.maxDevices || 0;

    return (
        <>
            <LoadingIcon isLoading={isLoading} showOverlay={true} />

            {/* Site Admin: Public Key Management */}
            {isSiteAdmin && (
                <Paper sx={{ p: 2, m: 1 }}>
                    <div className="license-management-header">
                        <Typography variant="h6" gutterBottom>License Public Key</Typography>
                        {hasPublicKey && !isEditingKey && (
                            <Button variant="outlined" size="small" onClick={() => setIsEditingKey(true)}>
                                Update Key
                            </Button>
                        )}
                    </div>
                    {isKeyLoading ? <LinearProgress /> : hasPublicKey && !isEditingKey ? (
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                            <Chip label="Public key is configured" color="success" />
                        </Box>
                    ) : (
                        <>
                            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                                {hasPublicKey
                                    ? 'Update the public key used to verify license JWT tokens.'
                                    : 'The public key used to verify license JWT tokens. This must be set before licenses can be applied.'}
                            </Typography>
                            <TextField
                                label="Public Key (PEM format)"
                                multiline
                                rows={6}
                                fullWidth
                                value={publicKey}
                                onChange={(e) => setPublicKey(e.target.value)}
                                placeholder="-----BEGIN PUBLIC KEY-----&#10;...&#10;-----END PUBLIC KEY-----"
                                sx={{ mb: 2, fontFamily: 'monospace' }}
                                slotProps={{ htmlInput: { style: { fontFamily: 'monospace', fontSize: '0.85rem' } } }}
                            />
                            <Box sx={{ display: 'flex', gap: 1 }}>
                                <Button variant="contained" onClick={handleSavePublicKey} disabled={isSaving || !publicKey.trim()}>
                                    Save Public Key
                                </Button>
                                {hasPublicKey && (
                                    <Button variant="outlined" onClick={() => { setIsEditingKey(false); fetchPublicKey(); }}>
                                        Cancel
                                    </Button>
                                )}
                            </Box>
                        </>
                    )}
                </Paper>
            )}

            {/* License Status & Token Input */}
            <Paper sx={{ p: 2, m: 1 }}>
                <div className="license-management-header">
                    <Typography variant="h6" gutterBottom>
                        License Management — {selectedOri}
                    </Typography>
                </div>

                {error && <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>{error}</Alert>}
                {successMessage && <Alert severity="success" sx={{ mb: 2 }} onClose={() => setSuccessMessage(null)}>{successMessage}</Alert>}

                {!isLoading && licenseInfo && (
                    <Box sx={{ mb: 3 }}>
                        <Box sx={{ display: 'flex', gap: 2, alignItems: 'center', mb: 2, flexWrap: 'wrap' }}>
                            <Chip
                                label={licenseInfo.licensed ? 'Licensed' : 'Unlicensed'}
                                color={licenseInfo.licensed ? 'success' : 'default'}
                            />
                            {licenseInfo.licensed && isExpired && (
                                <Chip label="Expired" color="error" />
                            )}
                            {licenseInfo.licensed && !isExpired && licenseInfo.exp && (
                                <Chip label={`Expires: ${new Date(licenseInfo.exp * 1000).toLocaleDateString()}`} color="info" variant="outlined" />
                            )}
                            {licenseInfo.licensed && (
                                <Chip label={`Devices: ${licensedCount} / ${maxDevices}`} color={licensedCount >= maxDevices ? 'warning' : 'info'} variant="outlined" />
                            )}
                            {licenseInfo.licensed && isSiteAdmin && (
                                <Button variant="outlined" color="error" size="small" onClick={handleDeleteLicense} disabled={isSaving}>
                                    Delete License
                                </Button>
                            )}
                        </Box>

                        {licenseInfo.licensed && licenseInfo.license && (
                            <Box sx={{ mb: 2, p: 2, bgcolor: 'action.hover', borderRadius: 1 }}>
                                <Typography variant="subtitle2" color="text.secondary">Registree</Typography>
                                <Typography variant="body2">
                                    {licenseInfo.license.registree.company} — {licenseInfo.license.registree.firstName} {licenseInfo.license.registree.lastName}
                                </Typography>
                                <Typography variant="body2" color="text.secondary">
                                    {licenseInfo.license.registree.email} | {licenseInfo.license.registree.phone}
                                </Typography>
                                {licenseInfo.license.features && licenseInfo.license.features.length > 0 && (
                                    <Box sx={{ mt: 1 }}>
                                        <Typography variant="subtitle2" color="text.secondary">Features</Typography>
                                        <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap' }}>
                                            {licenseInfo.license.features.map(f => (
                                                <Chip key={f} label={f} size="small" variant="outlined" />
                                            ))}
                                        </Box>
                                    </Box>
                                )}
                            </Box>
                        )}
                    </Box>
                )}

                <Typography variant="subtitle1" gutterBottom>Apply License Token</Typography>
                <TextField
                    label="License JWT Token"
                    multiline
                    rows={4}
                    fullWidth
                    value={licenseToken}
                    onChange={(e) => setLicenseToken(e.target.value)}
                    placeholder="Paste the license JWT token here..."
                    sx={{ mb: 2 }}
                    slotProps={{ htmlInput: { style: { fontFamily: 'monospace', fontSize: '0.85rem' } } }}
                />
                <Button variant="contained" onClick={handleApplyLicense} disabled={isSaving || !licenseToken.trim()}>
                    {licenseInfo?.licensed ? 'Update License' : 'Apply License'}
                </Button>
            </Paper>

            {/* Device Licensing */}
            {licenseInfo?.licensed && !isExpired && (
                <Paper sx={{ p: 2, m: 1 }}>
                    <div className="license-management-header">
                        <Typography variant="h6" gutterBottom>
                            Device Licensing
                        </Typography>
                        <Chip label={`${licensedCount} / ${maxDevices} licensed`} color={licensedCount >= maxDevices ? 'warning' : 'success'} />
                    </div>
                    <TableContainer>
                        <Table>
                            <TableHead>
                                <TableRow>
                                    <TableCell>Device Name</TableCell>
                                    <TableCell>Device ID</TableCell>
                                    <TableCell align="center">Licensed</TableCell>
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                {devices.map(device => (
                                    <TableRow key={device.deviceId}>
                                        <TableCell>{device.deviceName}</TableCell>
                                        <TableCell sx={{ fontFamily: 'monospace', fontSize: '0.85rem' }}>{device.deviceId}</TableCell>
                                        <TableCell align="center">
                                            <Switch
                                                checked={!!device.licensed}
                                                onChange={(e) => handleToggleDeviceLicense(device.deviceId, e.target.checked)}
                                                disabled={!device.licensed && licensedCount >= maxDevices}
                                            />
                                        </TableCell>
                                    </TableRow>
                                ))}
                                {devices.length === 0 && (
                                    <TableRow>
                                        <TableCell colSpan={3} align="center">
                                            <Typography variant="body2" color="text.secondary">No devices found for this agency.</Typography>
                                        </TableCell>
                                    </TableRow>
                                )}
                            </TableBody>
                        </Table>
                    </TableContainer>
                </Paper>
            )}
        </>
    );
};

export default LicenseManagementPage;
